import React, { useCallback, useEffect, useState } from "react";
import { formatDate, tradeGet, tradeJson } from "../../utils/tradeApi";

export default function TradeProjectManager({ onProjectsChanged }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setProjects(await tradeGet("/api/trade/admin/projects")); }
    catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function setEnabled(project) {
    const enabling = !project.tradeEnabled;
    const prompt = enabling
      ? `Enable Trade for ${project.name}? Default drawing packages will be created.`
      : `Disable Trade for ${project.name}? Subscribers will temporarily lose access, but files and settings will be preserved.`;
    if (!window.confirm(prompt)) return;
    setBusyId(project._id); setError(""); setMessage("");
    try {
      const result = enabling
        ? await tradeJson(`/api/trade/admin/projects/${project._id}/enable`, "POST")
        : await tradeJson(`/api/trade/admin/projects/${project._id}/status`, "PUT", { tradeEnabled: false });
      const created = Number(result.createdCategoryCount || 0);
      setMessage(enabling
        ? `Trade enabled for ${project.name}.${created ? ` ${created} default drawing packages created.` : ""}`
        : `Trade disabled for ${project.name}. Existing files and subscriber settings were preserved.`);
      await load();
      if (onProjectsChanged) await onProjectsChanged(project._id, enabling);
    } catch (requestError) { setError(requestError.message); }
    finally { setBusyId(""); }
  }

  return (
    <section className="trade-card trade-project-manager">
      <div className="trade-section-heading">
        <div><span className="trade-eyebrow trade-green">ADMIN</span><h2>Trade projects</h2><p>Enable existing projects for Trade access or temporarily disable them without deleting data.</p></div>
        <button className="trade-button secondary" onClick={load} disabled={loading}>Refresh</button>
      </div>
      {error && <div className="trade-alert error">{error}</div>}
      {message && <div className="trade-alert success">{message}</div>}
      {loading ? <div className="trade-loading">Loading projects…</div> : (
        <div className="trade-project-admin-list">
          {projects.map((project) => (
            <article className="trade-project-admin-row" key={project._id}>
              <div className="trade-project-admin-name">
                <strong>{project.name}</strong>
                <span className={`trade-status ${project.tradeEnabled ? "active" : "inactive"}`}>{project.tradeEnabled ? "Trade enabled" : "Not enabled"}</span>
                {project.description && <small>{project.description}</small>}
              </div>
              <dl className="trade-project-stats">
                <div><dt>Packages</dt><dd>{project.activeCategoryCount}</dd></div>
                <div><dt>Subscribers</dt><dd>{project.subscriberCount}</dd></div>
                <div><dt>Files</dt><dd>{project.fileCount}</dd></div>
                <div><dt>Pending</dt><dd>{project.pendingFileCount}</dd></div>
              </dl>
              <div className="trade-project-audit">
                {project.tradeEnabled
                  ? <small>Enabled {formatDate(project.tradeEnabledAt, true)}</small>
                  : <small>{project.tradeDisabledAt ? `Disabled ${formatDate(project.tradeDisabledAt, true)}` : "Trade has not been enabled"}</small>}
              </div>
              <button
                className={`trade-button ${project.tradeEnabled ? "secondary danger-outline" : "primary"}`}
                disabled={busyId === project._id}
                onClick={() => setEnabled(project)}
              >
                {busyId === project._id ? "Saving…" : project.tradeEnabled ? "Disable Trade" : "Enable Trade"}
              </button>
            </article>
          ))}
          {!projects.length && <div className="trade-empty">No master projects are available.</div>}
        </div>
      )}
    </section>
  );
}
